import { useCallback, useEffect, useRef } from 'react';

/**
 * Client-engagement tracking for the public proposal view (CRHQ 16687dd9).
 *
 * Zero UI, zero perceived latency: events are batched and sent with
 * navigator.sendBeacon (fetch keepalive fallback), so closing the tab never
 * loses the visit. If tracking fails or is blocked, the proposal loads
 * exactly the same — tracking is strictly additive.
 *
 * Privacy: no cookies, no fingerprints, no third parties. The visit is
 * grouped by a random per-tab sessionStorage key. Public-link visitors stay
 * fully anonymous; gated links get their verified email attached server-side.
 */

const DWELL_INTERVAL_SECONDS = 30;
const MAX_DWELL_SECONDS = 600; // cap "read time" at 10 minutes per visit
const FLUSH_AT = 8; // events
const FLUSH_EVERY_MS = 3000;

type TrackedEvent = {
  type: 'link_opened' | 'dwell_ping' | 'section_viewed' | 'hotel_details_viewed';
  sessionKey: string;
  metadata?: Record<string, string | number>;
};

function getSessionKey(token: string): string {
  const key = `pa_sk_${token.slice(0, 8)}`;
  try {
    const existing = sessionStorage.getItem(key);
    if (existing && /^[A-Za-z0-9_-]{8,64}$/.test(existing)) return existing;
    // random, not fingerprinted — identifies THIS tab's visit only
    const fresh = (crypto.randomUUID?.() ?? `${Date.now().toString(36)}${Math.random().toString(36).slice(2)}`)
      .replace(/[^A-Za-z0-9_-]/g, '')
      .slice(0, 32);
    sessionStorage.setItem(key, fresh);
    return fresh;
  } catch {
    // sessionStorage unavailable (private mode edge cases) — one in-memory visit
    return `mem${Math.random().toString(36).slice(2, 12)}`;
  }
}

function detectDevice(): 'mobile' | 'desktop' {
  try {
    return window.matchMedia('(pointer: coarse)').matches ? 'mobile' : 'desktop';
  } catch {
    return 'desktop';
  }
}

export function useProposalTracking(token: string | undefined, enabled: boolean) {
  const queueRef = useRef<TrackedEvent[]>([]);
  const openedRef = useRef(false);
  const seenSectionsRef = useRef<Set<string>>(new Set());
  const seenHotelsRef = useRef<Set<string>>(new Set());
  const dwellSecondsRef = useRef(0);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const flushTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const sessionKeyRef = useRef<string>('');

  const flush = useCallback(() => {
    if (flushTimerRef.current) {
      clearTimeout(flushTimerRef.current);
      flushTimerRef.current = null;
    }
    const batch = queueRef.current.splice(0, queueRef.current.length);
    if (!token || batch.length === 0) return;

    const body = JSON.stringify({ events: batch });
    try {
      // text/plain blob: same-origin, no preflight, survives tab close
      const blob = new Blob([body], { type: 'text/plain;charset=UTF-8' });
      if (navigator.sendBeacon?.(`/api/public/track/${token}`, blob)) return;
    } catch {
      /* fall through to fetch */
    }
    try {
      fetch(`/api/public/track/${token}`, {
        method: 'POST',
        headers: { 'Content-Type': 'text/plain;charset=UTF-8' },
        body,
        keepalive: true,
      }).catch(() => {});
    } catch {
      /* tracking must never break the page */
    }
  }, [token]);

  const enqueue = useCallback(
    (event: Omit<TrackedEvent, 'sessionKey'>) => {
      if (!token || !sessionKeyRef.current) return;
      queueRef.current.push({ ...event, sessionKey: sessionKeyRef.current });
      if (queueRef.current.length >= FLUSH_AT) {
        flush();
      } else if (!flushTimerRef.current) {
        flushTimerRef.current = setTimeout(flush, FLUSH_EVERY_MS);
      }
    },
    [token, flush],
  );

  // Set up the visit once the token is known
  useEffect(() => {
    if (!token) return;
    sessionKeyRef.current = getSessionKey(token);

    const onHide = () => {
      if (document.visibilityState === 'hidden') flush();
    };
    document.addEventListener('visibilitychange', onHide);
    window.addEventListener('pagehide', flush);
    return () => {
      document.removeEventListener('visibilitychange', onHide);
      window.removeEventListener('pagehide', flush);
      flush();
    };
  }, [token, flush]);

  // Dwell heartbeat — runs while the visit is active and the tab is visible
  useEffect(() => {
    if (!enabled || !token) return;
    if (timerRef.current) return;
    timerRef.current = setInterval(() => {
      if (document.visibilityState !== 'visible') return; // background tabs don't read
      if (dwellSecondsRef.current >= MAX_DWELL_SECONDS) {
        if (timerRef.current) clearInterval(timerRef.current);
        timerRef.current = null;
        return;
      }
      dwellSecondsRef.current += DWELL_INTERVAL_SECONDS;
      enqueue({ type: 'dwell_ping', metadata: { seconds: DWELL_INTERVAL_SECONDS } });
    }, DWELL_INTERVAL_SECONDS * 1000);
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [enabled, token, enqueue]);

  // The "opened" beacon — once per visit, as soon as the proposal has loaded
  useEffect(() => {
    if (!enabled || !token || openedRef.current) return;
    openedRef.current = true;
    enqueue({ type: 'link_opened', metadata: { device: detectDevice() } });
  }, [enabled, token, enqueue]);

  /** Section visibility — call when the client navigates to a proposal section. */
  const trackSection = useCallback(
    (section: string) => {
      if (!section || section === 'intro' || seenSectionsRef.current.has(section)) return;
      seenSectionsRef.current.add(section);
      enqueue({ type: 'section_viewed', metadata: { section } });
    },
    [enqueue],
  );

  /** Hotel engagement — call when the client opens a hotel's detail modal. */
  const trackHotelView = useCallback(
    (venueId: string) => {
      if (!venueId || seenHotelsRef.current.has(venueId)) return;
      seenHotelsRef.current.add(venueId);
      enqueue({ type: 'hotel_details_viewed', metadata: { venueId } });
    },
    [enqueue],
  );

  return { trackSection, trackHotelView };
}
